import { useContext, useEffect, useRef } from 'react';
import { VonageClientContext } from './vonage-client-provider';

/**
 * Hook to register a cleanup handler that runs when Vonage session errors occur.
 *
 * **Why this is needed:**
 * When a Vonage session becomes invalid (token expiration, network issues), any active
 * calls become "zombie calls" - the local UI thinks there's still a call, but the
 * server/session is dead. This hook lets components cleanup their state to prevent:
 * - Users being unable to answer new calls
 * - "No call found" / "invalid-member-state" errors
 * - UI stuck in wrong state
 *
 * **Architecture:**
 * Uses the Observer pattern - VonageClientProvider publishes session error events,
 * and components subscribe to cleanup their own state.
 *
 * @param handler - Function to call when session error occurs
 *
 * @example
 * ```tsx
 * // In VoiceCallActionsProvider
 * useVonageSessionError(async () => {
 *   if (callState !== 'idle') {
 *     // Cleanup SDK state
 *     await cleanupVonageCallState(client, { callId, reason: 'session_error' });
 *     // Clear React state
 *     callDispatch({ type: 'call:hangup', reason: 'session_error' });
 *   }
 * });
 * ```
 */
export function useVonageSessionError(handler: () => void | Promise<void>) {
  const context = useContext(VonageClientContext);

  if (!context) {
    throw new Error('useVonageSessionError must be used within a VonageClientProvider');
  }

  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const stableHandler = () => handlerRef.current();
    const cleanup = context.registerSessionErrorHandler(stableHandler);
    return cleanup;
  }, [context]);
}
